import BetaLabel from '@anm/components/BetaLabel';
import Switch, { SwitchWrapper } from '@anm/components/form/Switch';
import Image from '@anm/components/image/Image';
import { FC, MouseEvent, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import * as semrush from '@anm/helpers/semrush';
import routes from '../../../../routes';
import { ListElement } from '../../../layouts/LeftBarLayout';
import { LinkButton, NavButton, NavButtonVariant } from '../Wrapper';

import { ButtonProps } from '.';
import { getLinkTarget } from '@anm/helpers/upgradeLink';

const { Link } = routes;

export type ButtonElement = ListElement & {
  isActive: boolean;
  navButtonVariant?: NavButtonVariant;
  href?: string;
  onClick(evt: MouseEvent<Element>): void;
};

export type ButtonLinkElement = Omit<ButtonElement, 'href'> & {
  href: string;
};

const SwitchButtonWrapper = styled.div<{ disabled?: boolean }>`
  position: relative;
  margin-bottom: 10px;
  border-radius: 5px;
  z-index: 1;

  ${NavButton} {
    width: 100%;
    margin-bottom: 0;
  }
  ${SwitchWrapper} {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);

    label {
      padding-bottom: 0;
    }

    ${({ disabled }) =>
      disabled &&
      css`
        top: calc(50% + 5px);

        label {
          padding-bottom: 10px;
        }
      `}
  }

  &:hover {
    background: #f5f7fa;
  }
`;

const NavButtonDiv: FC<ButtonElement> = ({ button, isActive, onClick, navButtonVariant }) => (
  <NavButton isActive={isActive} data-title={button.title} onClick={onClick} {...{ navButtonVariant }}>
    {button.icon && <Image src={button.icon} alt={button.title} />}
    {button.isBeta && <BetaLabel positionVariant="top-right" />}
    <span dangerouslySetInnerHTML={{ __html: button.title }} />
  </NavButton>
);

const NavButtonLink: FC<ButtonLinkElement> = ({ button, isActive, onClick, navButtonVariant, href }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const isSemrush = semrush.isSemrushIframe();

  const onRightButtonClick = (ev: Event) => {
    ev.preventDefault();
    if (isSemrush) return;

    href && window.open(href, getLinkTarget());
    return false;
  };

  useEffect(() => {
    ref.current?.addEventListener('contextmenu', onRightButtonClick, false);

    return () => {
      ref.current?.removeEventListener('contextmenu', onRightButtonClick, false);
    };
  }, [isSemrush]);

  return (
    <LinkButton ref={ref}>
      <Link route={href}>
        <a data-omit="true">
          <ImageButton {...{ button, isActive, onClick, navButtonVariant }} />
        </a>
      </Link>
    </LinkButton>
  );
};

const ImageButton: FC<ButtonElement> = props => (
  <>{props.href ? <NavButtonLink {...(props as ButtonLinkElement)} /> : <NavButtonDiv {...props} />}</>
);

const CustomButton: FC<ButtonElement> = ({ button }) => button.renderButton?.()!;

const SwitchButton: FC<ButtonElement> = ({ button, isActive, onClick, navButtonVariant, href, ...rest }) => (
  <SwitchButtonWrapper disabled={button.switch?.disabled}>
    <ImageButton {...{ ...rest, button, isActive, onClick, navButtonVariant, href }} />
    <Switch checked={button.switch!.status} {...button.switch!} />
  </SwitchButtonWrapper>
);

const isCustomButton = ({ button }: ButtonElement) => !!button.renderButton;
const isSwitchButton = ({ button }: ButtonElement) => !!button.switch;

const DesktopButton: FC<ButtonElement> = props => {
  if (isCustomButton(props)) return <CustomButton {...props} />;
  else if (isSwitchButton(props)) return <SwitchButton {...props} />;

  return <ImageButton {...props} />;
};

const DesktopButtons: FC<ButtonProps> = ({ list, activeButton, onActiveButtonChange, navButtonVariant }) => (
  <>
    {list.map((item, index) => (
      <DesktopButton
        href={item.button.href}
        key={item.button.title}
        isActive={activeButton === index}
        onClick={item.button?.onClick || (() => onActiveButtonChange(index))}
        {...{ ...item, navButtonVariant }}
      />
    ))}
  </>
);

export default DesktopButtons;
